// src/pages/AdminDashboard.js

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  CircularProgress,
  Pagination,
  CardContent,
  Chip,
} from '@mui/material';
import { QrCode2, Logout, People, QrCode, Visibility, Add, ManageAccounts, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const packagesPerPage = 10; // Display 10 packages per page

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_API_URL}/get-all`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }

      const data = await response.json();
      // Sort packages by created date (newest first)
      const sortedData = Array.isArray(data) ? data.sort((a, b) => {
        const dateA = new Date(a.qr_created_at || a.created_at || 0);
        const dateB = new Date(b.qr_created_at || b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      }) : [];
      setPackages(sortedData);
      
      // Process data for monthly statistics
      const last4Months = processMonthlyStats(sortedData);
      setMonthlyStats(last4Months);
    } catch (error) {
      console.error('Error fetching packages:', error);

      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const processMonthlyStats = (packages) => {
    const months = [];
    const now = new Date();
    
    // Get last 4 months
    for (let i = 3; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(date.toLocaleString('default', { month: 'short', year: 'numeric' }));
    }

    const stats = months.map(month => {
      const count = packages.filter(pkg => {
        if (!pkg.qr_created_at) {
          return false;
        }
        const pkgDate = new Date(pkg.qr_created_at);
        if (isNaN(pkgDate.getTime())) {
          return false;
        }
        const pkgMonth = pkgDate.toLocaleString('default', { month: 'short', year: 'numeric' });

        return pkgMonth === month;
      }).length;

      return { month, count };
    });

    return stats;
  };

  const chartData = {
    labels: monthlyStats.map(stat => stat.month),
    datasets: [
      {
        label: 'Packages Created',
        data: monthlyStats.map(stat => stat.count),
        fill: false,
        borderColor: '#1F425C',
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Package Creation Trends',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  const handleCreateUser = () => {
    navigate('/signup');
  };

  const handleCreateQR = () => {
    navigate('/form');
  };

  const handleManageUsers = () => {
    navigate('/user-management');
  };

  const handleViewPackage = (packageId) => {
    navigate(`/hindalco?uuid=${packageId}`);
  };

  const handleDeletePackage = async (packageId) => {
    if (window.confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/delete?uuid=${packageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete package');
        }

        // Remove the deleted package from the packages state
        setPackages(prevPackages => prevPackages.filter(pkg => pkg.uuid !== packageId));
        
        // Update monthly stats after deletion
        const updatedPackages = packages.filter(pkg => pkg.uuid !== packageId);
        const updatedStats = processMonthlyStats(updatedPackages);
        setMonthlyStats(updatedStats);

        alert('Package deleted successfully');
      } catch (error) {
        console.error('Error deleting package:', error);
        alert('Failed to delete package. Please try again.');
      }
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const indexOfLastPackage = currentPage * packagesPerPage;
  const indexOfFirstPackage = indexOfLastPackage - packagesPerPage;
  const currentPackages = packages.slice(indexOfFirstPackage, indexOfLastPackage);
  const totalPages = Math.ceil(packages.length / packagesPerPage);

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafc',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress size={60} sx={{ color: '#1F425C' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppBar position="static" color="primary" elevation={0} sx={{ bgcolor: '#1F425C' }}>
        <Toolbar>
          <img src="/HINDALCO.NS_BIG.png" alt="Hindalco" style={{ marginRight: '16px', height: '32px', width: 'auto' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff', fontWeight: 500 }}>
            Hindalco QR Management
          </Typography>
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              ml: 2,
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, pb: 2 }}>
        <Grid container spacing={3} justifyContent="center">
          {/* Quick Actions Card */}
          <Grid item xs={12} md={6} lg={4}>
            <Paper elevation={2} sx={{
              height: '100%',
              bgcolor: '#fff',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1F425C', fontWeight: 600 }}>
                  Quick Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* <Button
                    variant="contained"
                    startIcon={<People />}
                    onClick={handleCreateUser}
                    fullWidth
                    sx={{
                      bgcolor: '#AC1E15',
                      '&:hover': { bgcolor: '#8B1612', transform: 'translateY(-2px)' },
                      py: 1.5,
                      borderRadius: 2,
                      transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    }}
                  >
                    Add New User
                  </Button> */}
                  <Button
                    variant="contained"
                    startIcon={<QrCode />}
                    onClick={handleCreateQR}
                    fullWidth
                    sx={{
                                        bgcolor: '#1F425C',
                  '&:hover': { bgcolor: '#1F425C', transform: 'translateY(-2px)' },
                      py: 1.5,
                      borderRadius: 2,
                      transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    }}
                  >
                    Generate QR Code
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ManageAccounts />}
                    onClick={handleManageUsers}
                    fullWidth
                    sx={{
                      bgcolor: '#AC1E15',
                      '&:hover': { bgcolor: '#8B1612', transform: 'translateY(-2px)' },
                      py: 1.5,
                      borderRadius: 2,
                      transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
                    }}
                  >
                    Manage Users
                  </Button>
                </Box>
              </CardContent>
            </Paper>
          </Grid>

          {/* Statistics Card */}
          <Grid item xs={12} md={6} lg={4}>
            <Paper elevation={2} sx={{
              height: '100%',
              bgcolor: '#fff',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1F425C', fontWeight: 600 }}>
                  Package Statistics
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{
                      textAlign: 'center',
                      p: 3,
                      bgcolor: '#E8F4FD',
                      borderRadius: 2,
                      border: '1px solid #93c5fd',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)',
                      },
                    }}>
                      <Typography variant="h3" sx={{ color: '#1F425C', fontWeight: 600 }} gutterBottom>
                        {packages.length}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Total Packages
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Paper>
          </Grid>

          {/* Chart */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                height: 240,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Line data={chartData} options={chartOptions} />
            </Paper>
          </Grid>

          {/* Packages Table */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h4" sx={{ color: '#1F425C', fontWeight: 600 }}>
                    Recent Packages
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                                    <TableRow sx={{ bgcolor: '#E8F4FD' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Package ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Location</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Delivery Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Items</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1F425C' }}>Actions</TableCell>
              </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentPackages.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#64748b' }}>
                            No packages found on this page.
                          </TableCell>
                        </TableRow>
                      ) : (
                        currentPackages.map((pkg) => (
                          <TableRow 
                            key={pkg.uuid} 
                            hover 
                            sx={{
                              '&:last-child td, &:last-child th': { border: 0 },
                            }}
                          >
                            <TableCell>{pkg.package_number}</TableCell>
                            <TableCell>{pkg.vendor_name}</TableCell>
                            <TableCell>{pkg.location || 'N/A'}</TableCell>
                            <TableCell>{pkg.owner || 'N/A'}</TableCell>
                            <TableCell>{new Date(pkg.delivery_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {pkg.items.map((item, i) => (
                                  <Chip key={i} label={item} size="small" sx={{ bgcolor: '#FDF6E3', color: '#92400e', border: '1px solid #F0CF55' }} />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>{pkg.qr_created_by}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => handleViewPackage(pkg.uuid)}
                                  sx={{
                                    borderColor: '#AC1E15',
                                    color: '#AC1E15',
                                    '&:hover': {
                                      borderColor: '#8B1612',
                                      bgcolor: 'rgba(230, 36, 38, 0.05)',
                                      transform: 'translateY(-2px)',
                                    },
                                    borderRadius: 2,
                                    transition: 'transform 0.2s ease-in-out, border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
                                  }}
                                >
                                  View
                                </Button>
                                <Button
                                  variant="outlined"
                                  startIcon={<Delete />}
                                  onClick={() => handleDeletePackage(pkg.uuid)}
                                  sx={{
                                    borderColor: '#d32f2f',
                                    color: '#d32f2f',
                                    '&:hover': {
                                      borderColor: '#b71c1c',
                                      bgcolor: 'rgba(211, 47, 47, 0.05)',
                                      transform: 'translateY(-2px)',
                                    },
                                    borderRadius: 2,
                                    transition: 'transform 0.2s ease-in-out, border-color 0.2s ease-in-out, background-color 0.2s ease-in-out',
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Paper>
          </Grid>
        </Grid>
      </Container>
      {packages.length > packagesPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 4 , paddingBottom: '100px'}}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#64748b',
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#1F425C',
                color: '#fff',
                
              },
              '& .MuiPaginationItem-root:hover': {
                backgroundColor: 'rgba(30, 58, 138, 0.1)',
                transform: 'translateY(-2px)',
              },
              transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
            }}
          />
        </Box>
      )}


    </Box>
  );
};

export default AdminDashboard;